/**
 * PC Studio Home：请求 card_metrics（logical_flag: fallback_latest_date）解析指标 time，
 * 构建 showTimeMap 并透传给子组件（与主工程 dashboard Service 解耦，仅封装请求与注入）。
 */
import React, { useMemo } from 'react';
import { useParams } from 'umi';
import moment from 'moment';
import Service from '@/pages/dashboard/components/PageContainer/Service';
import { TECHLAND_CARD_METRICS_API } from '@home/pages/studio/home/_constant';
import { resolveTechlandShowTimeMetricKey } from '@home/pages/studio/home/_utils';
import {
  applySpecialDealsFlag,
  mergeSpecialDealsTrackData,
} from '@home/pages/studio/home/utils/specialDealsRequest';

/** 与 CardState / Slot 读取 ShowTime 结果一致 */
export const SHOW_TIME_SERVICE_DATA_KEY = '_showTimeData';

const DEFAULT_FILTERS = {
  market_list: ['255'],
  os_list: ['255'],
  category_list: ['255'],
  product_list: ['255'],
  zone_list: ['255'],
  channel_list: ['255'],
  lang_list: ['255'],
  custom_filters: [
    { key: 'bundle', filter_list: ['255'] },
    { key: 'payment_type', filter_list: ['Paid only'] },
    { key: 'catalog', filter_list: ['255'] },
  ],
};

export interface IShowTimeServiceProps {
  api?: string;
  gameCode?: string;
  metrics?: string[];
  /**
   * 为 true 时按筛选 scope 请求 ShowTime 单指标（忽略 metrics）。
   * 指标名优先用 `showTimeMetricByScope[filter.scope]`，未配置时再用 `_utils` 内兜底映射。
   */
  scopeAwareShowTimeMetrics?: boolean;
  /** 与 HomeFilter scope 的 value 对齐，如 { "net_revenue": "profit", "gross_revenue": "gross_revenue" } */
  showTimeMetricByScope?: Record<string, string>;
  extraParams?: Record<string, unknown>;
  filters?: Record<string, unknown>;
  children?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  filterLoading?: boolean;
  trackData?: string[];
  data?: Record<string, unknown>;
  /** HomeFilter 经 PageContainer 注入，与 data.filter 合并 */
  filter?: Record<string, unknown>;
  filterVal?: Record<string, unknown>;
  /** 透传给 Service，开关打开时在请求中追加 special deals logical_flag */
  appendSpecialDealsFlag?: boolean;
  [key: string]: unknown;
}

function ShowTimeInject({
  child,
  data,
}: {
  child: React.ReactElement;
  data?: Record<string, unknown>;
}) {
  const serviceData = data?.[SHOW_TIME_SERVICE_DATA_KEY] as
    | { data?: Record<string, string>; loading?: boolean; }
    | undefined;
  const showTimeMap: Record<string, string> = serviceData?.data ?? {};
  const showTimeLoading = Boolean(serviceData?.loading);
  return React.cloneElement(child, {
    showTimeMap,
    showTimeLoading,
    data,
  } as any);
}

export default function ShowTimeService(props: IShowTimeServiceProps) {
  const {
    api = TECHLAND_CARD_METRICS_API,
    metrics = [],
    scopeAwareShowTimeMetrics,
    showTimeMetricByScope,
    extraParams,
    filters: configFilters,
    children,
    style,
    className,
    filterLoading: propsFilterLoading,
    trackData = ['filterInitData'],
    data: propsData,
    gameCode,
    filter: filterFromParent,
    filterVal: filterValFromParent,
    appendSpecialDealsFlag,
  } = props;
  const { studioId } = useParams() as { studioId?: string; };

  /** HomeFilter 只注入 props，Service 读的是 data.filter，需合并 */
  const serviceData = useMemo(
    () => ({
      ...(propsData ?? {}),
      filter:
        filterFromParent ??
        filterValFromParent ??
        (propsData as { filter?: unknown; } | undefined)?.filter,
    }),
    [propsData, filterFromParent, filterValFromParent],
  );

  const beforeRequest = (reqData: any) => {
    const { filterVal, filterInitData } = reqData || {};
    const fv = filterVal || {};
    const period = fv.date_type ?? 'daily';
    const resolvedMetrics = scopeAwareShowTimeMetrics
      ? [
        (typeof fv?.scope === 'string' &&
            showTimeMetricByScope?.[fv.scope]) ||
            resolveTechlandShowTimeMetricKey(fv.scope),
      ]
      : metrics;
    const params = {
      studio_code: studioId || 'techland',
      game_code: gameCode ?? 'techland_overview',
      filters:
        (filterInitData as Record<string, unknown> | undefined) ||
        (configFilters as Record<string, unknown>) ||
        DEFAULT_FILTERS,
      period,
      start_time: fv.start_time,
      end_time: fv.end_time,
      logical_flag: 'fallback_latest_date',
      metrics: resolvedMetrics,
      ...(extraParams || {}),
      is_confirm: false,
    };
    return applySpecialDealsFlag(params, reqData, appendSpecialDealsFlag);
  };

  const effectiveTrackData = useMemo(
    () => mergeSpecialDealsTrackData(trackData, appendSpecialDealsFlag),
    [trackData, appendSpecialDealsFlag],
  );

  const formatData = (res: any) => {
    const cardList: any[] = res?.card_list ?? [];
    const map: Record<string, string> = {};
    for (const item of cardList) {
      const name = item?.metrics_name;
      let rawTime = item?.time;
      if (
        rawTime === undefined ||
        rawTime === null ||
        String(rawTime).trim() === ''
      ) {
        const dod = item?.dod;
        if (dod && typeof dod === 'object' && 'this_time' in dod) {
          rawTime = (dod as { this_time?: string; }).this_time;
        }
      }
      if (!name || rawTime === undefined || rawTime === null) {
        continue;
      }
      const s = String(rawTime).trim();
      if (/^\d{8}$/.test(s)) {
        map[name] = `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
      } else {
        const d = moment(s);
        map[name] = d.isValid() ? d.format('YYYY-MM-DD') : s;
      }
    }
    return map;
  };

  const content = (
    <Service
      api={api}
      dataKey={SHOW_TIME_SERVICE_DATA_KEY}
      beforeRequest={beforeRequest}
      formatData={formatData}
      rawData
      filterLoading={propsFilterLoading}
      trackData={effectiveTrackData}
      data={serviceData}
    >
      {React.Children.map(children, child => {
        if (!React.isValidElement(child)) {
          return child;
        }
        return <ShowTimeInject child={child as React.ReactElement} />;
      })}
    </Service>
  );

  if (!className && !style) {
    return content;
  }

  return (
    <div className={className} style={style}>
      {content}
    </div>
  );
}
